import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { parseDate } from '@internationalized/date';
import PageContainer from '../../common_components/PageContainer';
import ConfirmActionModal from '../../common_components/ConfirmActionModal';
import AccessRestrictedContent from '../../common_components/AccessRestrictedContent';
import { toast } from '../../common_components/Toast/ToastUtils';
import { fetchScorerMeetings, fetchScorerOptions, deleteScorerMeeting, selectScorerGroups, selectScorerLoading, selectScorerDeleting, } from '../../redux/meetingScorerSlice';
import { setActiveModuleKey } from '../../redux/userDataSlice';
import { useLayout } from '../../contexts/LayoutContext';
import { apiErrorMessage } from '../../redux/meetingAgendaSlice';
import meetingScorerService from '../../services/meetingScorer.service';
import MeetingScorerHeader from './components/MeetingScorerHeader';
import MeetingFormDrawer from './components/MeetingFormDrawer';
import ViewMeetingDrawer from './components/ViewMeetingDrawer';
import LinkClientDrawer from './components/LinkClientDrawer';
import MeetingAgenda from './components/MeetingAgenda';
import ScorerTabs from './components/ScorerTabs';
import MeetingScorerReport from './MeetingScorerReport';
import { etTodayYmd, fmtLongYmd } from './meetingScorerUtils';
/** First day of the current month in US Eastern — the default month filter. */
const currentEtMonth = () => parseDate(etTodayYmd()).set({ day: 1 });
/** CalendarDate → "YYYY-MM" for the list query. */
const monthParam = (d) => `${d.year}-${String(d.month).padStart(2, '0')}`;
/**
 * Meeting Scorer — registered meetings for a month, as an agenda: status chips,
 * a Mon–Fri calendar and one collapsible section per day (see MeetingAgenda).
 * The nightly scorer matches every registered meeting to its Fireflies meeting
 * by name and scores it (or marks it a no-show); a scored meeting can no longer
 * be edited.
 *
 * Permissions: module access = view. "Manage Meetings" adds the Add Meetings
 * button, Edit (incl. scoring eligibility / no-show reason), Link to Client and
 * Send Schedule; "Delete Meeting" adds Delete; "Regenerate Report" adds
 * Regenerate Report; "Score Report" adds the Score Report tab — the server
 * enforces the same rules.
 */
const MODULE_KEY = 'meeting_scorer';
const MANAGE_MEETINGS = 'Manage Meetings';
const DELETE_MEETING = 'Delete Meeting';
const REGENERATE_REPORT = 'Regenerate Report';
/** Shows the Score Report tab */
const SCORE_REPORT = 'Score Report';
/** A month has at most 31 dates, so one request (the API's max of 50 dates) covers it. */
const DATES_PER_MONTH = 50;
const MeetingScorerList = () => {
    const dispatch = useDispatch();
    const groups = useSelector(selectScorerGroups);
    const loading = useSelector(selectScorerLoading);
    const deleting = useSelector(selectScorerDeleting);
    const permissions = useSelector((state) => state.auth.permissions);
    const modulePerms = permissions?.[MODULE_KEY];
    const hasAccess = modulePerms?.access !== false;
    const actions = Array.isArray(modulePerms?.actions) ? modulePerms.actions : [];
    const canManage = actions.includes(MANAGE_MEETINGS);
    const canDelete = actions.includes(DELETE_MEETING);
    const canRegenerate = actions.includes(REGENERATE_REPORT);
    const canViewScoreReport = actions.includes(SCORE_REPORT);
    const [view, setView] = useState('meetings');
    const [month, setMonth] = useState(currentEtMonth);
    const [clientId, setClientId] = useState('');
    const [amId, setAmId] = useState('');
    const [formOpen, setFormOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [viewTarget, setViewTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [regenerateTarget, setRegenerateTarget] = useState(null);
    const [linkTarget, setLinkTarget] = useState(null);
    const [regenerating, setRegenerating] = useState(false);
    const [scheduleDate, setScheduleDate] = useState(null);
    const [sendingSchedule, setSendingSchedule] = useState(false);
    const token = useSelector((state) => state.auth.token);
    // Collapse the left nav by default on this wide page; restore the prior state on leave.
    const { isSidebarExpanded, setIsSidebarExpanded } = useLayout();
    const sidebarWasExpanded = useRef(isSidebarExpanded);
    useEffect(() => {
        setIsSidebarExpanded(false);
        return () => setIsSidebarExpanded(sidebarWasExpanded.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // setActiveModuleKey is a plain action, so the key is in the store before
    // the fetches below read it for the x-module-key header.
    useEffect(() => {
        dispatch(setActiveModuleKey(MODULE_KEY));
        if (!hasAccess)
            return;
        dispatch(fetchScorerOptions());
    }, [dispatch, hasAccess]);
    const listParams = useCallback(() => ({
        page: 1,
        limit: DATES_PER_MONTH,
        client_id: clientId || undefined,
        am_id: amId || undefined,
        month: monthParam(month),
    }), [month, clientId, amId]);
    const loadMeetings = useCallback(async () => {
        try {
            await dispatch(fetchScorerMeetings(listParams())).unwrap();
        }
        catch (err) {
            toast.error(typeof err === 'string' ? err : 'Failed to load meetings');
        }
    }, [dispatch, listParams]);
    useEffect(() => {
        if (!hasAccess || view !== 'meetings')
            return;
        loadMeetings();
    }, [hasAccess, view, loadMeetings]);
    // While any meeting is regenerating, refresh quietly every 20s so its new
    // score appears without a manual reload.
    const anyRegenerating = groups.some((g) => g.meetings.some((m) => m.regenerating));
    useEffect(() => {
        if (!hasAccess || !anyRegenerating || view !== 'meetings')
            return;
        const timer = setInterval(() => {
            dispatch(fetchScorerMeetings({ ...listParams(), silent: true }));
        }, 20000);
        return () => clearInterval(timer);
    }, [dispatch, hasAccess, anyRegenerating, view, listParams]);
    /** Starts scoring the meeting again; the list then shows it as regenerating. */
    const handleRegenerateConfirm = async () => {
        if (!regenerateTarget)
            return;
        setRegenerating(true);
        try {
            const res = await meetingScorerService.regenerateMeeting(regenerateTarget.id, token || '');
            if (res?.data?.success) {
                toast.success(res.data.message || 'Regenerating the report.');
                setRegenerateTarget(null);
                loadMeetings();
            }
            else {
                toast.error(apiErrorMessage(res, 'Could not regenerate the report.'));
            }
        }
        catch {
            toast.error('Could not regenerate the report.');
        }
        finally {
            setRegenerating(false);
        }
    };
    /** Re-reads the Google Calendars for that day (today / tomorrow) — same as the scheduled sync, without AM emails. */
    const handleRefreshDay = async (date) => {
        try {
            const res = await meetingScorerService.refreshFromCalendar(date, token || '');
            if (res?.data?.success) {
                toast.success(res.data.message || 'Calendar refreshed.');
                await loadMeetings();
            }
            else {
                toast.error(apiErrorMessage(res, 'Could not refresh from the calendar.'));
            }
        }
        catch {
            toast.error('Could not refresh from the calendar.');
        }
    };
    /** Emails the selected day's external meetings schedule (grouped by AM). */
    const handleSendSchedule = async () => {
        if (!scheduleDate)
            return;
        setSendingSchedule(true);
        try {
            const res = await meetingScorerService.sendSchedule(scheduleDate, token || '');
            if (res?.data?.success) {
                toast.success(res.data.message || 'Schedule sent.');
                setScheduleDate(null);
            }
            else {
                toast.error(apiErrorMessage(res, 'Could not send the schedule.'));
            }
        }
        catch {
            toast.error('Could not send the schedule.');
        }
        finally {
            setSendingSchedule(false);
        }
    };
    const handleDeleteConfirm = async () => {
        if (!deleteTarget)
            return;
        try {
            const res = await dispatch(deleteScorerMeeting(deleteTarget.id)).unwrap();
            toast.success(res.message || 'Meeting deleted.');
            setDeleteTarget(null);
            loadMeetings();
        }
        catch (err) {
            toast.error(typeof err === 'string' ? err : 'Delete failed');
        }
    };
    if (!hasAccess) {
        return (_jsx(PageContainer, { children: _jsx(AccessRestrictedContent, {}) }));
    }
    // Without the "Score Report" action there is no tab switch (the header shows its title instead).
    const tabs = canViewScoreReport ? _jsx(ScorerTabs, { value: view, onChange: setView }) : undefined;
    if (view === 'report' && canViewScoreReport) {
        return (_jsx(PageContainer, { children: _jsx(MeetingScorerReport, { tabs: tabs, canDelete: canDelete }) }));
    }
    return (_jsxs(PageContainer, { children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1, height: 'calc(100vh - 90px)', overflow: 'hidden' }, children: [_jsx(MeetingScorerHeader, { leftSection: tabs, month: month, setMonth: setMonth, clientId: clientId, setClientId: setClientId, amId: amId, setAmId: setAmId, canManage: canManage, onAddMeetings: () => { setEditTarget(null); setFormOpen(true); } }), _jsx(MeetingAgenda, { groups: groups, loading: loading, month: month, canManage: canManage, canDelete: canDelete, canRegenerate: canRegenerate, onView: setViewTarget, onEdit: (m) => { setEditTarget(m); setFormOpen(true); }, onRegenerate: setRegenerateTarget, onViewReport: (m) => m.report_url && window.open(m.report_url, '_blank', 'noopener,noreferrer'), onLinkClient: setLinkTarget, onDelete: setDeleteTarget, onSendSchedule: setScheduleDate, onRefreshDay: handleRefreshDay })] }), _jsx(MeetingFormDrawer, { open: formOpen, meeting: editTarget, onClose: () => { setFormOpen(false); setEditTarget(null); }, onSaved: loadMeetings }), _jsx(ViewMeetingDrawer, { open: !!viewTarget, meeting: viewTarget, onClose: () => setViewTarget(null), canDelete: canDelete, onDeleted: () => { setViewTarget(null); loadMeetings(); } }), _jsx(LinkClientDrawer, { open: !!linkTarget, meeting: linkTarget, onClose: () => setLinkTarget(null), onSaved: loadMeetings }), _jsx(ConfirmActionModal, { open: !!scheduleDate, title: "Send Meetings Schedule", description: scheduleDate
                    ? `Email the external meetings schedule for ${fmtLongYmd(scheduleDate)}?`
                    : '', confirmText: "Send", onConfirm: handleSendSchedule, onClose: () => setScheduleDate(null), loading: sendingSchedule }), _jsx(ConfirmActionModal, { open: !!regenerateTarget, title: "Regenerate Report", description: regenerateTarget
                    ? `Score "${regenerateTarget.meeting_name}" on ${fmtLongYmd(regenerateTarget.date)} again with the current rules?`
                        + (regenerateTarget.scoring_status === 'scored' ? ' The current score and PDF report will be replaced.' : '')
                        + ' It takes about 1–2 minutes; no email is sent.'
                    : '', confirmText: "Regenerate", onConfirm: handleRegenerateConfirm, onClose: () => setRegenerateTarget(null), loading: regenerating }), _jsx(ConfirmActionModal, { open: !!deleteTarget, title: "Delete Meeting", description: deleteTarget
                    ? `Delete "${deleteTarget.meeting_name}" on ${fmtLongYmd(deleteTarget.date)}?` +
                        (deleteTarget.scoring_status === 'scored' ? ' Its score result and PDF report will be deleted too.' : '') +
                        ' This cannot be undone.'
                    : '', confirmText: "Delete", onConfirm: handleDeleteConfirm, onClose: () => setDeleteTarget(null), loading: deleting, isDeleteIcon: true })] }));
};
export default MeetingScorerList;
